/**
 * Avataaars-based avatar system.
 *
 * Uses the direct https://avataaars.io HTTP API which (unlike DiceBear v10)
 * actually honours every variant parameter — so we can do real modular
 * customisation (hair, eyes, mouth, clothes, accessories, …).
 *
 * The avatar config object stored alongside each user is a plain map of
 * Avataaars option keys, e.g. `{ topType: "ShortHairShortFlat", hairColor: "Brown", … }`.
 */
import React from "react";

const API = "https://avataaars.io";

// ---- Option catalogues ---------------------------------------------------

export const TOP_TYPE_M = [
  "ShortHairShortFlat", "ShortHairShortRound", "ShortHairShortCurly",
  "ShortHairShortWaved", "ShortHairSides", "ShortHairTheCaesar",
  "ShortHairTheCaesarSidePart", "ShortHairFrizzle", "ShortHairShaggyMullet",
  "ShortHairDreads01", "ShortHairDreads02", "NoHair",
];
export const TOP_TYPE_F = [
  "LongHairStraight", "LongHairStraight2", "LongHairStraightStrand",
  "LongHairBob", "LongHairBun", "LongHairCurly", "LongHairCurvy",
  "LongHairBigHair", "LongHairDreads", "LongHairFrida", "LongHairFro",
  "LongHairFroBand", "LongHairMiaWallace", "LongHairNotTooLong",
  "LongHairShavedSides",
];
export const TOP_TYPE_HAT = ["Hat", "Hijab", "Turban", "WinterHat1", "WinterHat2", "WinterHat3", "WinterHat4", "Eyepatch"];

export const ACCESSORIES = ["Blank", "Round", "Sunglasses", "Wayfarers", "Prescription01", "Prescription02", "Kurt"];

export const HAIR_COLOR = ["Black", "Brown", "BrownDark", "Auburn", "Blonde", "BlondeGolden", "Red", "PastelPink", "Platinum", "SilverGray"];
export const HAIR_COLOR_HEX = {
  Black: "#272727", Brown: "#A55728", BrownDark: "#724133", Auburn: "#A55728",
  Blonde: "#B58143", BlondeGolden: "#D6B370", Red: "#C93305",
  PastelPink: "#F59797", Platinum: "#ECDCBF", SilverGray: "#E8E1E1",
};

export const FACIAL_HAIR = ["Blank", "BeardLight", "BeardMedium", "BeardMajestic", "MoustacheFancy", "MoustacheMagnum"];

export const CLOTHE_TYPE = ["ShirtCrewNeck", "ShirtScoopNeck", "ShirtVNeck", "Hoodie", "BlazerShirt", "BlazerSweater", "CollarSweater", "GraphicShirt", "Overall"];
export const CLOTHE_COLOR = ["Black", "Blue01", "Blue02", "Blue03", "Gray01", "Gray02", "Heather", "PastelBlue", "PastelGreen", "PastelOrange", "PastelRed", "PastelYellow", "Pink", "Red", "White"];
export const CLOTHE_COLOR_HEX = {
  Black: "#262E33", Blue01: "#65C9FF", Blue02: "#5199E4", Blue03: "#25557C",
  Gray01: "#E6E6E6", Gray02: "#929598", Heather: "#3C4F5C",
  PastelBlue: "#B1E2FF", PastelGreen: "#A7FFC4", PastelOrange: "#FFDEB5",
  PastelRed: "#FFAFB9", PastelYellow: "#FFFFB1", Pink: "#FF488E",
  Red: "#FF5C5C", White: "#FFFFFF",
};

export const EYE_TYPE = ["Default", "Happy", "Wink", "WinkWacky", "Squint", "Surprised", "Hearts", "Side", "Close", "Cry", "Dizzy", "EyeRoll"];
export const EYEBROW_TYPE = ["Default", "DefaultNatural", "RaisedExcited", "RaisedExcitedNatural", "UpDown", "UpDownNatural", "FlatNatural", "Angry", "AngryNatural", "SadConcerned", "SadConcernedNatural", "UnibrowNatural"];
export const MOUTH_TYPE = ["Default", "Smile", "Twinkle", "Tongue", "Eating", "Serious", "Sad", "Concerned", "Disbelief", "Grimace", "ScreamOpen", "Vomit"];

export const SKIN_COLOR = ["Tanned", "Yellow", "Pale", "Light", "Brown", "DarkBrown", "Black"];
export const SKIN_COLOR_HEX = {
  Tanned: "#FD9841", Yellow: "#F8D25C", Pale: "#FFDBB4", Light: "#EDB98A",
  Brown: "#D08B5B", DarkBrown: "#AE5D29", Black: "#614335",
};

// Background tints (not natively supported — drawn behind via CSS).
export const BG_SWATCHES = [
  "#EF4444", "#F97316", "#F59E0B", "#22C55E", "#0EA5E9",
  "#6366F1", "#EC4899", "#A855F7", "#FDE68A", "#1E293B", "#FFFFFF",
];

// ---- Default config & URL builder ----------------------------------------

export const DEFAULT_AVATAR = {
  topType: "ShortHairShortFlat",
  accessoriesType: "Blank",
  hairColor: "Brown",
  facialHairType: "Blank",
  facialHairColor: "Brown",
  clotheType: "ShirtCrewNeck",
  clotheColor: "Blue03",
  eyeType: "Default",
  eyebrowType: "Default",
  mouthType: "Smile",
  skinColor: "Light",
  gender: "M",
  bg: "#FDE68A",
};

export const buildAvataaarsUrl = (cfg = {}) => {
  const a = { ...DEFAULT_AVATAR, ...cfg };
  const params = new URLSearchParams({
    avatarStyle: "Circle",
    topType: a.topType,
    accessoriesType: a.accessoriesType,
    hairColor: a.hairColor,
    facialHairType: a.facialHairType,
    facialHairColor: a.facialHairColor,
    clotheType: a.clotheType,
    clotheColor: a.clotheColor,
    eyeType: a.eyeType,
    eyebrowType: a.eyebrowType,
    mouthType: a.mouthType,
    skinColor: a.skinColor,
  });
  // GraphicShirt requires an extra param. Pick a default if user didn't choose.
  if (a.clotheType === "GraphicShirt") {
    params.set("graphicType", a.graphicType || "Bat");
  }
  return `${API}/?${params.toString()}`;
};

// ---- React rendering helper ----------------------------------------------

// Memoized so it doesn't rebuild the SVG URL + wrapper markup on every parent
// keystroke. Since `cfg` is a stable reference until the user changes their
// avatar and `size` is a constant number, the shallow comparison of React.memo
// short-circuits re-renders whenever the parent (e.g. VsRobot / VsHumano
// setup screens) re-renders due to unrelated state (name / school typing).
export const AvatarSVG = React.memo(function AvatarSVG({ cfg, size = 64 }) {
  const url = buildAvataaarsUrl(cfg);
  const bg = (cfg && cfg.bg) || DEFAULT_AVATAR.bg;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: bg,
        overflow: "hidden",
        display: "grid",
        placeItems: "center",
      }}
    >
      <img
        src={url}
        alt="avatar"
        draggable={false}
        style={{ width: "100%", height: "100%", display: "block" }}
      />
    </div>
  );
});

// Alias for places that want a "preview" mental model — same component.
export const AvatarPreview = AvatarSVG;

